import React, { useState, useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import Hero from '../components/home/Hero';
import CallToAction from '../components/home/CallToAction';
import SEO from '../components/common/SEO';
import LogoSlider from '../components/home/LogoSlider';
import { wpApi, utils } from '../services/wordpressApi';
import { proxiedMediaUrl } from '../services/mediaProxy';
import '../styles/pages.css';

interface FeaturedContent {
  title: string;
  content: string;
  imageUrl: string;
  imageAlt: string;
  speakerLink: string;
}

// Default/fallback content
const DEFAULT_CONTENT: FeaturedContent = {
  title: 'Ready to Transform your Event?',
  content: 'CEO Keynotes brings visionary leaders like <a href="/speaker/mick-ebeling"><u>Mick Ebeling</u></a> from Not Impossible Labs to deliver moving keynote speeches for organizations of all sizes. Our network of influential CEOs and founders transforms events into unforgettable experiences.',
  imageUrl: '/featured/mick-ebeling.jpeg',
  imageAlt: 'Mick Ebeling speaking on stage',
  speakerLink: '/speaker/mick-ebeling'
};

// Timeout duration in milliseconds (3 seconds)
const API_TIMEOUT = 3000;

const Home: React.FC = () => {
  const [featuredContent, setFeaturedContent] = useState<FeaturedContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Helper to remove ravespeakers.com from text/html
  const cleanSourceDomain = (text: string) => {
    if (!text) return '';
    return text.replace(/https?:\/\/(?:www\.)?ravespeakers\.com/gi, '');
  };

  useEffect(() => {
    const fetchFeaturedContent = async () => {
      try {
        setIsLoading(true);
        
        // Create a timeout promise
        const timeoutPromise = new Promise<null>((resolve) => {
          setTimeout(() => {
            console.log('API timeout reached, using fallback content');
            resolve(null);
          }, API_TIMEOUT);
        });

        // Race between API call and timeout
        const data = await Promise.race([
          wpApi.getFeaturedContent(),
          timeoutPromise
        ]);
        
        if (data && data.title && data.content) {
          // Extract speaker link from content if available
          const linkMatch = data.content.rendered.match(/<a[^>]+href="([^"]+)"/);
          const speakerLink = linkMatch ? linkMatch[1] : DEFAULT_CONTENT.speakerLink;
          
          setFeaturedContent({
            title: utils.stripHTML(data.title.rendered),
            content: cleanSourceDomain(data.content.rendered),
            imageUrl: (() => {
              const fm = (data as any).featured_media;
              if (typeof fm === 'number' && fm > 0) return proxiedMediaUrl(fm, 'large');
              // Last resort (may expose origin):
              return (data as any).featured_image_url || DEFAULT_CONTENT.imageUrl;
            })(),
            imageAlt: utils.stripHTML(data.title.rendered) + ' featured speaker',
            speakerLink: speakerLink
          });
        } else {
          // Use default content if API returns no data
          setFeaturedContent(DEFAULT_CONTENT);
        }
      } catch (error) {
        console.error('Error loading featured content:', error);
        // Use default content on error
        setFeaturedContent(DEFAULT_CONTENT);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedContent();
  }, []);

  // Use the content to display (either fetched or default)
  const displayContent = featuredContent || DEFAULT_CONTENT;

  return (
    <>
      <SEO
        title="CEO Keynotes - Transform Your Event with World-Class Speakers"
        description="Book world-class keynote speakers for corporate events, conferences, and meetings. Our curated network of industry leaders will inspire and educate your audience. Get your quote today."
        keywords="keynote speakers, corporate events, motivational speakers, business conferences, leadership speakers, executive coaching, speaker booking, CEO speakers, business leaders"
        type="website"
        url="/"
      />
      
      <div className="home-page">
        {/* Hero Section with Rotating Speakers */}
        <Hero />

        {/* CEO Speaking Section - 100vh */}
        <Box
          sx={{
            maxHeight: '100vh',
            backgroundColor: '#000000',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: { xs: 4, md: 8 },
            py: 8,
          }}
        >
          {/* Main Content */}
          <Box sx={{ maxWidth: '1200px', mx: 'auto', mb: 4}}>
            {!isLoading && (
              <>
                <Typography
                  className="home-hero-title"
                >
                  {displayContent.title}
                </Typography>

                {/* Image and caption moved below paragraph */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    px: { xs: 1, md: 1 },
                    mt: 4,
                  }}
                >
                  <a href={displayContent.speakerLink}>
                    <img
                      src={displayContent.imageUrl}
                      alt={displayContent.imageAlt}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        maxHeight: '400px',
                        objectFit: 'cover',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(255, 255, 255, 0.1)',
                      }}
                    />
                  </a>
                </Box>

                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    px: { xs: 2, md: 4 },
                  }}
                >
                </Box>
                <Typography
                  className="home-description"
                  sx={{
                    fontFamily: '"Newsreader", serif',
                  }}
                  dangerouslySetInnerHTML={{ __html: displayContent.content }}
                />
              </>
            )}
          </Box>



        </Box>

        {/* Call to Action Section */}
        <CallToAction title={"Start your Search"} />

        {/* Logo Slider positioned below the CTA */}
        <Box sx={{ mt: 0, backgroundColor: '#000000', py: 4 }}>
          <LogoSlider />
        </Box>
      </div>
    </>
  );
};

export default Home; 